import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Shield, Menu, X, Phone } from "lucide-react";

/**
 * Navbar VEA STUDIO - Cinematic Dark Premium
 * - Transparente en haut, opaque noire au scroll
 * - Scroll progress bar or en haut de page
 * - Indicateur de section active : trait rouge bordeaux sous le lien
 * - Pas de curseur personnalisé
 */
interface NavbarProps {
  topBanner?: React.ReactNode;
}

export default function Navbar({ topBanner }: NavbarProps = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 60);

      // Calcul de la progression de lecture
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollY / docHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/portfolio", label: "Portfolio" },
    { href: "/blog", label: "Blog" },
    { href: "/disponibilites", label: "Disponibilités" },
    { href: "/tarifs", label: "Tarifs" },
    { href: "/services", label: "Devis" },
  ];

  const isActive = (href: string) =>
    href === "/" ? location === "/" : location.startsWith(href);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled
          ? "rgba(10, 10, 10, 0.97)"
          : "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(201,168,76,0.12)" : "none",
        boxShadow: scrolled ? "0 4px 30px rgba(0,0,0,0.5)" : "none",
      }}
    >
      {/* Bandeau optionnel au-dessus de la navbar */}
      {topBanner && (
        <div className="w-full">{topBanner}</div>
      )}

      {/* Scroll progress bar - fine ligne or en haut */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "2px",
          width: `${scrollProgress}%`,
          background: "linear-gradient(90deg, var(--vea-or), var(--vea-or-clair))",
          transition: "width 0.1s linear",
          zIndex: 60,
          pointerEvents: "none",
        }}
      />

      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex-shrink-0 group">
          <img
            src="/vea-logo-or.png"
            alt="VEA STUDIO - Vidéaste drone professionnel Bordeaux"
            width={820}
            height={492}
            loading="eager"
            /* w-auto : sans elle, l'attribut width={820} impose sa largeur et la
               page defile lateralement sur mobile (1005 px pour 390 px). */
            className="h-10 md:h-12 w-auto object-contain transition-all duration-500"
            style={{ filter: 'drop-shadow(0 0 0px rgba(201,168,76,0))' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 0 12px rgba(201,168,76,0.5))';
              (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 0 0px rgba(201,168,76,0))';
              (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/logo-noir.png";
            }}
          />
        </a>

        {/* Navigation desktop */}
        <nav className="hidden md:flex items-center gap-7 ml-auto mr-6">
          {navLinks.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="relative font-semibold text-xs uppercase tracking-widest transition-colors duration-300"
              style={{
                color: isActive(href) ? "var(--vea-or)" : "rgba(255,255,255,0.85)",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {label}
              {/* Indicateur section active - trait rouge bordeaux */}
              <span
                style={{
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  height: "1px",
                  width: isActive(href) ? "100%" : "0%",
                  background: "var(--vea-or)",
                  transition: "width 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              />
              {/* Hover : trait or */}
              <span
                className="nav-hover-line"
                style={{
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  height: "1px",
                  width: "0%",
                  background: "var(--vea-or)",
                  transition: "width 0.3s ease",
                  opacity: isActive(href) ? 0 : 1,
                }}
              />
            </Link>
          ))}
        </nav>

        {/* CTA + Admin */}
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:0643166706" className="group">
            <button
              className="btn-shimmer flex items-center gap-2 px-5 py-2.5 rounded-full text-[#0D0D0D] font-bold text-xs uppercase tracking-widest transition-all duration-300"
              style={{
                background: "var(--vea-or)",
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.12em",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "#E8C96A";
                (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 20px rgba(201,168,76,0.4)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "var(--vea-or)";
                (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
              }}
            >
              <Phone className="w-3.5 h-3.5" />
              Contactez-moi
            </button>
          </a>
          <Link href="/admin/login">
            <button
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-medium uppercase tracking-wider transition-all duration-300"
              style={{
                border: "1px solid rgba(19,67,61,0.6)",
                color: "#4CAF8A",
                fontFamily: "'Montserrat', sans-serif",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "#4CAF8A";
                (e.currentTarget as HTMLButtonElement).style.background = "rgba(76,175,138,0.06)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(19,67,61,0.6)";
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              }}
            >
              <Shield className="w-3.5 h-3.5" />
              Admin
            </button>
          </Link>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          <a href="tel:0643166706">
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[#0D0D0D] font-bold text-xs uppercase tracking-widest"
              style={{ background: "var(--vea-or)" }}
            >
              <Phone className="w-3.5 h-3.5" />
              Contact
            </button>
          </a>
          <button
            className="p-2 text-white transition-colors"
            style={{ color: "rgba(255,255,255,0.85)" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--vea-or)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.85)"; }}
            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div
          className="md:hidden border-t"
          style={{
            background: "rgba(10,10,10,0.98)",
            borderColor: "rgba(201,168,76,0.12)",
          }}
        >
          <nav className="container mx-auto px-4 py-5 flex flex-col gap-1">
            {navLinks.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                className="font-semibold py-3 px-4 text-sm uppercase tracking-widest transition-all duration-200"
                style={
                  isActive(href)
                    ? {
                        color: "var(--vea-or)",
                        borderLeft: "2px solid var(--vea-or)",
                        background: "rgba(201,168,76,0.06)",
                        fontFamily: "'Montserrat', sans-serif",
                      }
                    : {
                        color: "rgba(255,255,255,0.8)",
                        fontFamily: "'Montserrat', sans-serif",
                      }
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {label}
              </Link>
            ))}
            <Link
              href="/admin/login"
              className="font-medium py-3 px-4 flex items-center gap-2 text-sm"
              style={{ color: "#4CAF8A", fontFamily: "'Montserrat', sans-serif" }}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Shield className="w-4 h-4" />
              Admin
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
